<template>
    <div class="questionnaire-section">
        <div
            class="hidden-anchor"
            :id="'accordion-section-' + section.id"></div>
        <div
            class="page-container"
            role="tablist">
            <div
                class="page-header"
                role="tab">
                <b-button
                    class="btn-questionnaire-section-toggle"
                    block
                    variant="outline-primary"
                    @click="collapse()"
                    :class="collapsed && 'active'">
                    <div
                        class="page-title"
                        v-if="section.title">
                        {{ section.title }}
                    </div>

                    <div
                        class="page-title"
                        v-else>
                        Sectie {{ section.order }}
                    </div>

                    <i class="fas fa-fw fa-chevron-down state-icon"></i>

                    <div class="context-nav context-sub-assignment-page-nav">
                        <b-dropdown
                            class="ellipsis-nav sub-assignment-page-nav"
                            no-caret
                            variant="primary"
                            right
                            toggle-class="sub-assignment-page-context-menu">
                            <template v-slot:button-content>
                                <i class="fas fa-ellipsis-v"></i>
                            </template>

                            <b-dropdown-item
                                class="btn-edit edit-sub-assignment-page"
                                v-b-modal="'questionnaire-section-title-edit-modal' + section.id"
                                @click.prevent.stop="editTitle(section, $event)">
                                {{ $t('questionnaire.edit_title') }}
                            </b-dropdown-item>

                            <design-2021-questionnaire-edit-section-title-modal-component
                                :section-in="section"></design-2021-questionnaire-edit-section-title-modal-component>

                            <b-dropdown-item
                                class="remove-sub-assignment-page"
                                @click.prevent.stop="deleteSection(section)">
                                <i class="btn-remove"></i>{{ $t('questionnaire.delete_section') }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </b-button>
            </div>

            <b-collapse
                class="questionnaire-section-content"
                v-model="collapsed"
                :id="`accordion-section-${section.id}`"
                role="tabpanel"
                visible>
                <div class="description">
                    <label class="col-sm-3">{{ $t('common.description') }}</label>
                    <ckeditor
                        v-model="description"
                        :id="`questionnaire-section-description-${section.id}`"
                        :editor="editor"
                        :config="editorConfig"
                        @blur="updateSectionDescription()"></ckeditor>
                </div>

                <design-2021-questionnaire-edit-question-component
                    v-for="question in section.questions"
                    :key="question.id"
                    :question="question"
                    :section="section"></design-2021-questionnaire-edit-question-component>

                <div class="new-questionnaire-question">
                    <b-button
                        class="btn-add"
                        v-b-modal="'questionnaire-edit-add-question-modal-' + section.id"
                        variant="primary">
                        {{ $t('questionnaire.new_question') }}
                    </b-button>

                    <design-2021-questionnaire-edit-add-question-modal-component
                        :section="section"></design-2021-questionnaire-edit-add-question-modal-component>
                </div>
            </b-collapse>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import ApprenticeEditor from '../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-questionnaire-edit-section-component',
    props: ['section'],
    components: { draggable },
    data() {
        return {
            editor: ApprenticeEditor,
            editorConfig: {
                contentsCss: 'test',
                bodyClass: 'mybodyclass',
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'bulletedList',
                        'numberedList',
                        'blockQuote',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
            },
            description: this.section.description !== null ? this.section.description : '',
            collapsed: false,
        };
    },
    methods: {
        collapse() {
            this.collapsed = !this.collapsed;
            const name = 'questionaire-edit-section-collapse-' + this.section.id;
            localStorage.setItem(name, this.collapsed ? 1 : 0);
        },
        deleteSection(section) {
            this.alert
                .confirm(
                    this.$t('questionnaire.delete_section'),
                    this.$t('questionnaire.delete_section_confirmation_body'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.questionnaireSectionDelete(section.id)).then(
                        (response) => {
                            section.remove();

                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },

        updateSectionDescription() {
            const data = {
                description: this.description,
                id: this.section.id,
            };

            this.axios.post(this.routes.questionnaireSectionDescription(), data).then(
                (response) => {
                    this.section.update({ description: this.description });
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },
    mounted() {
        const name = 'questionnaire-edit-section-collapse-' + this.section.id;
        if (localStorage.getItem(name) !== null) {
            this.collapsed = !!parseInt(localStorage.getItem(name));
        } else if (this.section.order === 1) {
            this.collapsed = true;
        }
    },
};
</script>

<style scoped lang="scss">
.context-sub-assignment-page-nav ::v-deep {
    .sub-assignment-page-nav {
        .sub-assignment-page-context-menu {
            line-height: 1;
        }

        .dropdown-menu {
            border: 1px solid $border-color;
        }

        .edit-sub-assignment-page {
            &:before {
                position: relative;
                left: 3px;
                width: 20px;
                margin-right: 12px;
            }
        }

        .copy-sub-assignment-page {
            i {
                color: $primary;
            }
        }

        .remove-sub-assignment-page {
            i {
                color: $danger;
            }
        }

        .remove-sub-assignment-page,
        .copy-sub-assignment-page {
            font-size: $default-font-size;

            .dropdown-item {
                display: flex;
                align-items: center;

                i {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                    font-size: $base-font-size;
                }
            }
        }
    }
}

.hidden-anchor {
    position: relative;
    top: -110px;
    visibility: hidden;
}

.questionnaire-section-content {
    padding: map-get($grid-gutter-spacing, 'fifteen');

    .description {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }
}

.page-header {
    padding: 0;
    margin-bottom: 0;
    min-height: unset;
}

.questionnaire-section {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .btn-questionnaire-section-toggle {
        justify-content: space-between;
        border: 1px solid $primary;
        outline: none;
        box-shadow: none;
        border-radius: $border-radius-content;
        line-height: 1;
        padding: map-get($grid-gutter-spacing, 'fifteen');

        &,
        .title {
            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .state-icon {
            transform-origin: center;
            transition: all 0.8s ease;
            margin-left: auto;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        &:focus {
            box-shadow: none;
        }

        &:hover {
            background-color: $info;
            font-weight: $font-weight-bold;
            box-shadow: none;

            &,
            .sub-assignment-drag-handle {
                color: $white;
            }
        }

        &.not-collapsed {
            background-color: $info;
            font-weight: $font-weight-bold;
            border: 1px solid $info;
            color: $white;

            .state-icon {
                transform: rotate(-180deg);
            }
        }
    }
}

.new-questionnaire-question {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
}
</style>
